import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Grid2,
    useTheme,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { HEALTH_LABEL, HEALTH_TYPE, HEALTH_UNIT } from "../../helper/constant";
import moment from "moment";
import { formatDateTime } from "../../helper/global";
import SleepDataChart from "../SleepDataChart/SleepDataChart";

const METRIC_COLORS = {
    [HEALTH_TYPE.BODY_MASS]: "#8A2BE2", // Primary - dark gray
    [HEALTH_TYPE.BODY_FAT_PERCENTAGE]: "#f44336", // Error - red
    [HEALTH_TYPE.BMI]: "#4CAF50", // Success - green
    [HEALTH_TYPE.LEAN_BODY_MASS]: "#2196F3", // Info - blue
    [HEALTH_TYPE.SLEEP_ANALYSIS]: "#FF9800", // Warning - orange
};

const METRIC_ORDER = [
    HEALTH_TYPE.BODY_MASS,
    HEALTH_TYPE.BODY_FAT_PERCENTAGE,
    HEALTH_TYPE.BMI,
    HEALTH_TYPE.LEAN_BODY_MASS,
    HEALTH_TYPE.SLEEP_ANALYSIS,
];

export default function HealthDataChart({
    theme,
    healthData = [],
    onPointClick,
    patient,
    includeWeightManagement = true,
}) {
    const [selectedMetric, setSelectedMetric] = useState(HEALTH_TYPE.BODY_MASS);
    const [chartData, setChartData] = useState([]);

    useEffect(() => {
        const copyData = [...healthData];

        if (
            patient &&
            patient.healthData &&
            patient.healthData.startingWeight &&
            patient.healthData.startingWeightDate &&
            includeWeightManagement
        ) {
            const start = {
                date: patient.healthData.startingWeightDate,
                bodyMass: patient.healthData.startingWeight,
                startingWeight: true,
            };
            copyData.unshift(start);
        }

        setChartData(copyData);
    }, [healthData, patient, includeWeightManagement]);

    // Sort data by date ascending and filter out invalid entries
    const sortedData = [...(chartData || [])]
        .filter(
            (d) =>
                d &&
                d[selectedMetric] !== null &&
                d[selectedMetric] !== undefined,
        )
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

    const handleMetricChange = (event, newMetric) => {
        // Prevent deselecting all buttons
        if (newMetric !== null) {
            setSelectedMetric(newMetric);
        }
    };

    // Get min and max values for y-axis
    const currentData = sortedData
        .map((d) => {
            const value = parseFloat(d[selectedMetric]);
            return isNaN(value) || value == 0 ? null : value;
        })
        .filter((v) => v !== null);

    // Set default min/max if no valid data
    const minY =
        currentData.length > 0 ? Math.floor(Math.min(...currentData)) : 0;
    const maxY =
        currentData.length > 0 ? Math.ceil(Math.max(...currentData)) : 100;

    const currentColor = METRIC_COLORS[selectedMetric];

    // Create single series for selected metric
    const series = [
        {
            data: currentData.length > 0 ? currentData : [],
            label: HEALTH_LABEL[selectedMetric],
            valueFormatter: (v) =>
                v !== null && !isNaN(v)
                    ? `${v}${HEALTH_UNIT[selectedMetric]}`
                    : "No data",
            color: currentColor,
            showMark: true,
            area: false,
        },
    ];

    // Show empty state if no valid data
    // if (currentData.length === 0) {
    // return <></>;
    // }

    const xLabels = sortedData
        .map((d) => {
            const extra = d.startingWeight ? "(Weight Management) " : "";

            return parseFloat(d[selectedMetric])
                ? `${extra}${formatDateTime(d.date, "MMM D, YYYY", false)}`
                : null;
        })
        .filter((v) => v !== null);

    return (
        <Card>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Grid2
                        container
                        spacing={{ xs: 2, md: 2 }}
                        sx={{ width: "100%" }}
                    >
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Typography variant="h6">Health Metrics</Typography>
                        </Grid2>
                        <Grid2
                            size={{ xs: 12, md: 6 }}
                            display={"flex"}
                            justifyContent={{ xs: "center", md: "right" }}
                        >
                            <ToggleButtonGroup
                                exclusive
                                value={selectedMetric}
                                onChange={handleMetricChange}
                                aria-label="health metrics"
                                size="small"
                            >
                                {METRIC_ORDER.map((metric) => (
                                    <ToggleButton
                                        key={metric}
                                        value={metric}
                                        aria-label={HEALTH_LABEL[
                                            metric
                                        ].toLowerCase()}
                                        sx={{
                                            "&.Mui-selected": {
                                                backgroundColor:
                                                    theme.palette[metric]
                                                        ?.main ||
                                                    METRIC_COLORS[metric],
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor:
                                                        theme.palette[metric]
                                                            ?.dark ||
                                                        METRIC_COLORS[metric],
                                                },
                                            },
                                        }}
                                    >
                                        {HEALTH_LABEL[metric]}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Grid2>
                    </Grid2>
                </Box>
                <Box sx={{ width: "100%" }}>
                    {selectedMetric === HEALTH_TYPE.SLEEP_ANALYSIS && (
                        <SleepDataChart theme={theme} healthData={healthData} />
                    )}
                    {selectedMetric !== HEALTH_TYPE.SLEEP_ANALYSIS && (
                        <LineChart
                            series={series}
                            xAxis={[
                                {
                                    data: xLabels,
                                    scaleType: "point",
                                    tickLabelStyle: {
                                        angle: 0,
                                        textAnchor: "start",
                                        fontSize: 11,
                                    },
                                    valueFormatter: (date, context) => {
                                        if (
                                            date
                                                .toLowerCase()
                                                .includes("management")
                                        ) {
                                            const split = date.split(") ");
                                            date = split[1];

                                            const split2 = date.split(", ");
                                            return `${split2[0]}\n${split2[1]}\n${split[0]})`;
                                        }
                                        const split = date.split(", ");
                                        return `${split[0]}\n${split[1]}`;
                                    },
                                    tickInterval: (date, index) => {
                                        if (xLabels.length < 15) {
                                            return true;
                                        }

                                        if (
                                            xLabels.length > 15 &&
                                            xLabels.length < 50
                                        ) {
                                            return index % 5 === 0;
                                        }

                                        if (
                                            xLabels.length > 50 &&
                                            xLabels.length < 100
                                        ) {
                                            return index % 10 === 0;
                                        }

                                        return index % 15 === 0;
                                    },
                                },
                            ]}
                            yAxis={[
                                {
                                    min: minY - 1,
                                    max: maxY + 1,
                                    tickNumber: 8,
                                    labelStyle: {
                                        transform: "translateX(-10px)",
                                    },
                                },
                            ]}
                            height={300}
                            margin={{
                                left: 50,
                                right: 50,
                                top: 20,
                                bottom: 60,
                            }}
                            onClick={(event, pointData) => {
                                if (pointData && onPointClick) {
                                    const dataPoint =
                                        sortedData[pointData.dataIndex];
                                    onPointClick(dataPoint);
                                }
                            }}
                            sx={{
                                "& .MuiLineElement-root": {
                                    strokeWidth: 2,
                                },
                                "& .MuiMarkElement-root": {
                                    fill:
                                        currentData.length > 40
                                            ? "transparent"
                                            : currentColor,
                                    strokeWidth: 1,
                                    stroke: "transparent",
                                },
                                "& .MuiMarkElement-root:hover": {
                                    transform: "scale(1.5)",
                                    cursor: "pointer",
                                },
                            }}
                        />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
